import logger from '@/lib/logger';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Minus, Play, Trash, PencilSimple, Check, X, Barbell, Timer, FunnelSimple, MagnifyingGlass } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BODY_PART_LABELS = {
  full_body: 'Full Body', abs: 'Abs', arms: 'Arms', chest: 'Chest',
  legs: 'Legs', back: 'Back', shoulders: 'Shoulders'
};

export default function CustomWorkoutBuilder() {
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [customWorkouts, setCustomWorkouts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('list');
  const [editId, setEditId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [exRes, cwRes] = await Promise.all([
        axios.get(`${API}/workouts/library`),
        axios.get(`${API}/workouts/custom`),
      ]);
      setExercises(exRes.data);
      setCustomWorkouts(cwRes.data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExercise = (ex) => {
    setSelected(prev =>
      prev.find(s => s.id === ex.id)
        ? prev.filter(s => s.id !== ex.id)
        : [...prev, ex]
    );
  };

  const moveExercise = (index, dir) => {
    const arr = [...selected];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[index], arr[newIdx]] = [arr[newIdx], arr[index]];
    setSelected(arr);
  };

  const saveWorkout = async () => {
    if (!name.trim() || selected.length === 0) return;
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/workouts/custom/${editId}`, {
          name, description, exercise_ids: selected.map(s => s.id),
        });
      } else {
        await axios.post(`${API}/workouts/custom`, {
          name, description, exercise_ids: selected.map(s => s.id),
        });
      }
      setMode('list');
      setName('');
      setDescription('');
      setSelected([]);
      setEditId(null);
      fetchData();
    } catch (err) {
      logger.error(err);
    } finally {
      setSaving(false);
    }
  };

  const deleteWorkout = async (id) => {
    if (!window.confirm('Delete this custom workout?')) return;
    try {
      await axios.delete(`${API}/workouts/custom/${id}`);
      fetchData();
    } catch (err) {
      logger.error(err);
    }
  };

  const editWorkout = (w) => {
    setEditId(w.id);
    setName(w.name);
    setDescription(w.description || '');
    setSelected(w.exercises || []);
    setMode('create');
  };

  const startWorkout = (w) => {
    navigate('/workouts/player', { state: { exercises: w.exercises } });
  };

  const filtered = exercises.filter(ex => {
    if (filter !== 'all' && ex.body_part !== filter) return false;
    if (search && !ex.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const estimatedMin = selected.reduce((s, ex) => s + (ex.sets || 3) * 2, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (mode === 'create') {
    return (
      <div data-testid="custom-builder-create">
        <div className="mb-6">
          <button onClick={() => { setMode('list'); setEditId(null); setSelected([]); }} className="text-[#A1A1AA] hover:text-white text-sm mb-3 flex items-center gap-1">
            <X className="w-4 h-4" /> Cancel
          </button>
          <div className="label-tactical text-[#FF3B30] mb-1">{editId ? 'Edit Workout' : 'New Workout'}</div>
          <h1 className="text-4xl font-black tracking-tighter">BUILD YOUR WORKOUT</h1>
        </div>

        {/* Name & Description */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="label-tactical block mb-2">Workout Name</label>
            <input
              data-testid="custom-workout-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Morning Burn"
              className="input-tactical w-full"
              required
            />
          </div>
          <div>
            <label className="label-tactical block mb-2">Description (optional)</label>
            <input
              data-testid="custom-workout-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Quick full body session"
              className="input-tactical w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Exercise Picker */}
          <div>
            <h3 className="text-lg font-bold tracking-tight mb-3">SELECT EXERCISES</h3>
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-3">
              {['all', 'full_body', 'abs', 'arms', 'chest', 'legs', 'back', 'shoulders'].map(bp => (
                <button key={bp} onClick={() => setFilter(bp)}
                  className={`px-2 py-1 text-xs font-bold uppercase tracking-wider transition-all ${filter === bp ? 'bg-[#FF3B30] text-white' : 'bg-[#141414] border border-white/10 text-[#A1A1AA]'}`}>
                  {BODY_PART_LABELS[bp] || 'All'}
                </button>
              ))}
            </div>
            <div className="relative mb-3">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] w-4 h-4" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search exercises..." className="input-tactical pl-10 text-sm w-full" />
            </div>
            <div className="max-h-[400px] overflow-y-auto space-y-1 scrollbar-hide">
              {filtered.map(ex => {
                const isSelected = selected.find(s => s.id === ex.id);
                return (
                  <button key={ex.id} onClick={() => toggleExercise(ex)} data-testid={`pick-exercise-${ex.id}`}
                    className={`w-full text-left p-3 flex items-center justify-between transition-all ${isSelected ? 'bg-[#FF3B30]/10 border border-[#FF3B30]/30' : 'bg-[#141414] border border-white/5 hover:border-white/15'}`}>
                    <div>
                      <span className="font-medium text-sm">{ex.name}</span>
                      <span className="text-xs text-[#71717A] ml-2">{BODY_PART_LABELS[ex.body_part]}</span>
                    </div>
                    {isSelected ? <Check weight="bold" className="w-4 h-4 text-[#FF3B30]" /> : <Plus weight="bold" className="w-4 h-4 text-[#71717A]" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected Exercises (Ordering) */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold tracking-tight">YOUR WORKOUT ({selected.length})</h3>
              <span className="text-sm text-[#71717A]">~{estimatedMin} min</span>
            </div>
            {selected.length === 0 ? (
              <div className="tactical-card p-8 text-center">
                <Barbell weight="bold" className="w-10 h-10 text-[#71717A] mx-auto mb-2" />
                <p className="text-sm text-[#71717A]">Pick exercises from the left to build your workout</p>
              </div>
            ) : (
              <div className="space-y-1 mb-4">
                {selected.map((ex, i) => (
                  <div key={ex.id} className="tactical-card p-3 flex items-center gap-3" data-testid={`selected-exercise-${i}`}>
                    <span className="text-xs text-[#FF3B30] font-bold w-5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm truncate block">{ex.name}</span>
                      <span className="text-xs text-[#71717A]">{ex.sets}x{ex.reps}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveExercise(i, -1)} disabled={i === 0} className="p-1 text-[#71717A] hover:text-white disabled:opacity-20">
                        <span className="text-xs">UP</span>
                      </button>
                      <button onClick={() => moveExercise(i, 1)} disabled={i === selected.length - 1} className="p-1 text-[#71717A] hover:text-white disabled:opacity-20">
                        <span className="text-xs">DN</span>
                      </button>
                      <button onClick={() => toggleExercise(ex)} className="p-1 text-[#71717A] hover:text-[#FF3B30]">
                        <Minus weight="bold" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={saveWorkout}
              disabled={saving || !name.trim() || selected.length === 0}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="save-custom-workout"
            >
              <Check weight="bold" className="w-5 h-5" />
              {saving ? 'Saving...' : editId ? 'Update Workout' : 'Save Workout'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="custom-workout-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="label-tactical text-[#FF3B30] mb-1">Build</div>
          <h1 className="text-4xl font-black tracking-tighter">CUSTOM WORKOUTS</h1>
          <p className="text-[#A1A1AA] mt-2">Create and save your own workout routines.</p>
        </div>
        <button onClick={() => setMode('create')} className="btn-primary px-5 py-3 flex items-center gap-2" data-testid="new-custom-workout">
          <Plus weight="bold" className="w-5 h-5" /> New Workout
        </button>
      </div>

      {customWorkouts.length === 0 ? (
        <div className="tactical-card p-12 text-center">
          <Barbell weight="bold" className="w-14 h-14 text-[#71717A] mx-auto mb-4" />
          <h3 className="text-xl font-bold tracking-tight mb-2">NO CUSTOM WORKOUTS YET</h3>
          <p className="text-[#A1A1AA] mb-6">Build your first routine by selecting exercises from the library.</p>
          <button onClick={() => setMode('create')} className="btn-primary px-6 py-3">
            Build Your First Workout
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {customWorkouts.map((w, i) => (
            <div key={w.id} className="tactical-card p-5 hover:border-white/20 transition-all" data-testid={`custom-workout-card-${i}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-xl font-bold tracking-tight">{w.name}</h3>
                  {w.description && <p className="text-sm text-[#A1A1AA] mt-1">{w.description}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => editWorkout(w)} className="p-1.5 text-[#71717A] hover:text-white" data-testid={`edit-workout-${i}`}>
                    <PencilSimple weight="bold" className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteWorkout(w.id)} className="p-1.5 text-[#71717A] hover:text-[#FF3B30]" data-testid={`delete-workout-${i}`}>
                    <Trash weight="bold" className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm text-[#71717A] mb-3">
                <span className="flex items-center gap-1"><Barbell className="w-4 h-4" /> {w.exercise_count} exercises</span>
                <span className="flex items-center gap-1"><Timer className="w-4 h-4" /> ~{w.estimated_minutes} min</span>
              </div>
              <div className="flex flex-wrap gap-1 mb-4">
                {(w.exercises || []).slice(0, 4).map((ex, j) => (
                  <span key={ex.id || ex.name} className="text-xs bg-[#1C1C1E] border border-white/5 px-2 py-0.5">{ex.name}</span>
                ))}
                {(w.exercises || []).length > 4 && (
                  <span className="text-xs text-[#71717A]">+{w.exercises.length - 4} more</span>
                )}
              </div>
              <button onClick={() => startWorkout(w)} className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid={`start-custom-${i}`}>
                <Play weight="fill" className="w-4 h-4" /> Start Workout
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
